/**
 * "ALL" master checkbox — portals into FullCalendar's
 * `.fc-timegrid-axis-frame` so it sits on the same row as the day
 * checkboxes. Uses Kula Checkbox + Tooltip. Tooltip is scoped to
 * the checkbox only; the "ALL" label is not a hover target.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';

interface AllDaysCheckboxProps {
  hostElement: HTMLElement | null;
  isChecked: boolean;
  isIndeterminate: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  onToggle: (_checked: boolean) => void;
}

export function AllDaysCheckbox({
  hostElement,
  isChecked,
  isIndeterminate,
  isDisabled,
  disabledReason,
  onToggle,
}: AllDaysCheckboxProps) {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!hostElement) return;
    const find = () =>
      hostElement.querySelector<HTMLElement>('.fc-timegrid-axis-frame');

    // Subscribe to FullCalendar's async rendering of `.fc-timegrid-
    // axis-frame` via MutationObserver so `setTarget` only fires from
    // a subscription callback, never synchronously inside the effect
    // body. The microtask-deferred initial check handles the common
    // case where the node is already present when we mount.
    const applyTarget = () => setTarget(find());
    const observer = new MutationObserver(applyTarget);
    observer.observe(hostElement, { childList: true, subtree: true });
    queueMicrotask(applyTarget);

    return () => observer.disconnect();
  }, [hostElement]);

  if (!target) return null;

  const checkboxEl = (
    <Checkbox
      size="sm"
      checked={isChecked}
      indeterminate={isIndeterminate}
      disabled={isDisabled}
      onChange={(e) => onToggle(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const checkboxWithTooltip =
    isDisabled && disabledReason ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Box as="span" display="inline-flex" lineHeight={0}>
              {checkboxEl}
            </Box>
          </TooltipTrigger>
          <TooltipContent side="right">{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      checkboxEl
    );

  const inner = (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="4px"
      h="100%"
      w="100%"
      py="10px"
    >
      <KText
        fontSize="11px"
        fontWeight="500"
        textTransform="uppercase"
        color="secondary-500"
      >
        ALL
      </KText>
      {checkboxWithTooltip}
    </Flex>
  );

  return createPortal(inner, target);
}
